import {
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  Clock,
  MessageCircle,
  Plus,
} from "lucide-react";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";

import NewAppointmentModal from "@/components/NewAppointmentModal";
import TimeOffModal from "@/components/TimeOffModal";
import {
  StatusBadge,
  Button,
  Card,
  EmptyState,
  InfoBanner,
  PageTitle,
  Spinner,
} from "@/components/ui";
import { addDays, isoDate } from "@/lib/format";
import { useFormat, useT, type Chave } from "@/lib/i18n";
import { useAppointmentAction, useAppointments } from "@/lib/queries";
import type { Appointment, Me } from "@/lib/types";

/** Ações a partir de cada estado — espelha TRANSITIONS do backend.
 *
 *  `label` é chave, não texto: a tabela é constante de módulo e
 *  congelaria o idioma do import. Ver Agenda.tsx. */
const ACTIONS: Record<string, { action: string; label: Chave; variant?: "danger" }[]> = {
  pending: [
    { action: "confirm", label: "action.confirm" },
    { action: "cancel", label: "action.cancel", variant: "danger" },
  ],
  confirmed: [
    { action: "start", label: "action.start" },
    { action: "no-show", label: "action.noShow", variant: "danger" },
  ],
  in_progress: [{ action: "complete", label: "action.complete" }],
};

/**
 * A tela que o técnico abre no celular entre um serviço e outro.
 *
 * Prioridades, nesta ordem (docs/03-regras-agenda.md §4):
 *   1. ver a agenda do dia em um toque
 *   2. marcar um cliente dele
 *   3. bloquear um horário
 *
 * Por isso é lista vertical e não grade: em tela de 375px, grade de
 * horários não se lê.
 */
export default function MyAgenda() {
  const { money, hhmm, fullDate } = useFormat();
  const t = useT();
  const me = useOutletContext<Me>();
  const [date, setDate] = useState(new Date());
  const [creating, setCreating] = useState(false);
  const [blocking, setBlocking] = useState(false);

  const day = isoDate(date);
  const hoje = day === isoDate(new Date());

  const { data, isLoading } = useAppointments({
    date_from: day,
    date_to: day,
    professional: me.professional_id ?? "",
  });

  const act = useAppointmentAction();
  const agendamentos = data?.results ?? [];
  const ativos = agendamentos.filter((a) => !["canceled", "no_show"].includes(a.status));
  const proximo = ativos.find((a) => new Date(a.starts_at) > new Date());
  const total = ativos.reduce((s, a) => s + Number(a.total_price), 0);

  if (!me.professional_id) {
    return (
      <div className="p-4 lg:p-8">
        <PageTitle title={t("nav.myAgenda")} />
        <InfoBanner tone="neutral">
          {/* `<b>Equipe</b>` no meio da frase: o dicionário guarda a
              marcação junto, porque em inglês ela cai em outro ponto. */}
          <span dangerouslySetInnerHTML={{ __html: t("mine.noProfileText") }} />
        </InfoBanner>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8">
      <PageTitle
        eyebrow={hoje ? t("agenda.todayShort") : undefined}
        title={t("nav.myAgenda")}
        subtitle={fullDate(date.toISOString())}
      />

      {/* navegação de dia — alvos grandes, é uso no celular */}
      <div className="mb-4 flex items-center gap-2">
        <Button
          variant="secondary"
          onClick={() => setDate(addDays(date, -1))}
          aria-label={t("agenda.prevDay")}
          className="px-3"
        >
          <ChevronLeft size={18} />
        </Button>
        <input
          type="date"
          value={day}
          onChange={(e) => setDate(new Date(`${e.target.value}T12:00:00`))}
          className="flex-1 rounded-[4px] border border-line bg-carbon-900 px-3 py-2.5 text-center text-steel"
        />
        <Button
          variant="secondary"
          onClick={() => setDate(addDays(date, 1))}
          aria-label={t("agenda.nextDay")}
          className="px-3"
        >
          <ChevronRight size={18} />
        </Button>
        {!hoje && (
          <Button variant="ghost" onClick={() => setDate(new Date())}>
            {t("agenda.todayShort")}
          </Button>
        )}
      </div>

      {/* resumo: o profissional quer saber quantos e quanto */}
      <div className="mb-4 grid grid-cols-2 gap-3">
        <Card className="p-4">
          <p className="text-xs text-steel-dim">{t("agenda.appointments")}</p>
          <p className="mt-1 font-display text-3xl font-bold">{ativos.length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-steel-dim">{t("mine.production")}</p>
          <p className="mt-1 font-display text-3xl font-bold text-racing-500">
            {money(total)}
          </p>
        </Card>
      </div>

      {hoje && proximo && (
        <Card className="mb-4 border-racing-600/40 bg-racing-500/5 p-4">
          <p className="eyebrow mb-1">{t("mine.next")}</p>
          <p className="font-medium">
            {hhmm(proximo.starts_at)} · {proximo.customer_detail.name}
          </p>
          <p className="text-sm text-steel-muted">
            {proximo.items.map((i) => i.service_name).join(" + ")}
          </p>
        </Card>
      )}

      <div className="mb-4 flex gap-2">
        <Button className="flex-1" onClick={() => setCreating(true)}>
          <Plus size={18} />
          {t("mine.bookCustomer")}
        </Button>
        <Button variant="secondary" onClick={() => setBlocking(true)}>
          <Clock size={18} />
          {t("mine.block")}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-8" />
        </div>
      ) : agendamentos.length === 0 ? (
        <Card>
          <EmptyState
            icon={<CalendarCheck size={40} />}
            title={t("mine.freeDay")}
            description={t("mine.freeDayText")}
          />
        </Card>
      ) : (
        <div className="space-y-2">
          {agendamentos.map((a) => (
            <Linha
              key={a.id}
              appointment={a}
              onAction={(action) => act.mutate({ id: a.id, action })}
              busy={act.isPending}
            />
          ))}
        </div>
      )}

      <NewAppointmentModal
        open={creating}
        onClose={() => setCreating(false)}
        me={me}
        defaultDate={day}
      />
      <TimeOffModal
        open={blocking}
        onClose={() => setBlocking(false)}
        professionalId={me.professional_id}
        defaultDate={day}
      />
    </div>
  );
}

function Linha({
  appointment: a,
  onAction,
  busy,
}: {
  appointment: Appointment;
  onAction: (action: string) => void;
  busy: boolean;
}) {
  const { money, hhmm, phone } = useFormat();
  const pais = useOutletContext<Me>().garage.region.country;
  const t = useT();
  const actions = ACTIONS[a.status] ?? [];
  const wa = `https://wa.me/${a.customer_detail.phone.replace(/\D/g, "")}`;
  const encerrado = ["canceled", "no_show", "completed"].includes(a.status);

  return (
    <Card className={encerrado ? "opacity-55" : undefined}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="font-display text-2xl font-bold tabular-nums">
                {hhmm(a.starts_at)}
              </span>
              <span className="text-sm text-steel-dim">até {hhmm(a.ends_at)}</span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {a.vehicle_detail && (
                <span className="plate">{a.vehicle_detail.formatted_plate}</span>
              )}
              <span className="truncate text-base font-medium">
                {a.customer_detail.name}
              </span>
            </div>
            <p className="truncate text-sm text-steel-muted">
              {a.items.map((i) => i.service_name).join(" + ")}
              {a.vehicle_detail && ` · ${a.vehicle_detail.label}`}
            </p>
          </div>
          <div className="shrink-0 text-right">
            <StatusBadge status={a.status} />
            <p className="mt-2 font-semibold">{money(a.total_price)}</p>
          </div>
        </div>

        {a.customer_notes && (
          <p className="mt-3 rounded-[4px] border border-line bg-carbon-900 px-3 py-2 text-sm text-steel-muted">
            “{a.customer_notes}”
          </p>
        )}

        {a.customer_detail.no_show_count > 0 && !encerrado && (
          <p className="mt-2 text-xs text-red-400">
            {t("mine.missedTimes", { n: a.customer_detail.no_show_count })}
          </p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <a
            href={wa}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-[4px] border border-line px-3 py-2 text-sm text-emerald-400 transition hover:border-emerald-800"
          >
            <MessageCircle size={15} />
            {phone(a.customer_detail.phone, pais)}
          </a>
          {actions.map((action) => (
            <Button
              key={action.action}
              size="sm"
              variant={action.variant ?? "secondary"}
              disabled={busy}
              onClick={() => onAction(action.action)}
            >
              {t(action.label)}
            </Button>
          ))}
        </div>
      </div>
    </Card>
  );
}
